import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { AIRemarksContainer } from "../../remarks/components/aiRemarksContainer";
import { AnalysisRemarkKeys } from "../../remarks/config";
import { ProfileIdentityVerificationContainer } from "./ProfileIdentityVerificationContainer";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";

import { getCurrentCaseLRIds } from "../../caseinfo/logic/Slice";
import { fetchBorrowerVerifications } from "../../../thunks/AnalysisStep";

import { useCaseNotes } from "../../../hooks/useCaseNotes";
import { CaseNoteType } from "../../../api/CaseNote";

import { InlineLoadingSpinner } from "../../../components/ui/spinners/InlineLoadingSpinner";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import { AutoSelectVerticalNav } from "../../../components/ui/navigation/AutoSelectVerticalNav";

import { TabItem } from "@/src/components/ui/navigation/VerticalNavPanel";
import InterviewUnSavedChangesModal from "./InterviewFindings/ChangesModal";
import { selectIdentityProfileVerificationState, selectLoansAndBorrowers } from "../logic/verifications/verificationSlice";
import { RETRIEVING_DATA_SUBTITLE } from "../../../lib/constants";

const BORROWER_NAME_UNAVAILABLE_TEXT = "Name unavailable";

const ProfileIdentityAccordionContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id: case_id } = useParams<{ id: string }>();

  const loansAndBorrowers = useAppSelector(selectLoansAndBorrowers); 

  const loanReferenceIds = useAppSelector(getCurrentCaseLRIds);

  const [activeLoanReferenceId, setActiveLoanReferenceId] = useState<string>();
  const [activeBorrowerId, setActiveBorrowerId] = useState<string>();

  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const loadingState = useAppSelector(selectIdentityProfileVerificationState);

  useEffect(() => {
    if (activeBorrowerId && activeLoanReferenceId) {
      return;
    }

    if(!activeLoanReferenceId && loansAndBorrowers && loansAndBorrowers[0]){
      setActiveLoanReferenceId(loansAndBorrowers[0].loanReferenceId);
    }

    const loan = loansAndBorrowers.find((loanData) => loanData.loanReferenceId === activeLoanReferenceId);

    const primary_borrower = loan?.borrowers?.find(borrower => borrower.is_primary);

    if(primary_borrower) {
      setActiveBorrowerId(primary_borrower.borrower_id);
    } else if (loan?.borrowers){
      setActiveBorrowerId(loan?.borrowers[0].borrower_id);
    }

  }, [loansAndBorrowers, activeLoanReferenceId]);

  const handleRetry = () => {
    if (!case_id || !loanReferenceIds) return;

    dispatch(
      fetchBorrowerVerifications({
        case_id,
        loan_reference_ids: loanReferenceIds,
      })
    );
  };

  const { initialValue, ...remarksProps } = useCaseNotes({
    noteType: CaseNoteType.profile_verification,
  });

  const menuItems: ReadonlyArray<TabItem> = loansAndBorrowers.map((loan) => {

    const primary_borrowers = loan.borrowers?.filter((borrower) => borrower.is_primary) || [];
    const co_borrowers = loan.borrowers?.filter((borrower) => !borrower.is_primary) || [];

    const tabItem: TabItem =  {
      id: loan.loanReferenceId,
      header: primary_borrowers && primary_borrowers[0] && primary_borrowers[0]?.name? primary_borrowers[0].name : BORROWER_NAME_UNAVAILABLE_TEXT,
      subheader: `LR: ${loan.loanReferenceId}`,
      isSelectable: true,
      children: [...primary_borrowers, ...co_borrowers].map((borrower) => ({
        id: borrower.borrower_id,
        header: borrower.name ?? BORROWER_NAME_UNAVAILABLE_TEXT,
      })),
    }

    return tabItem
  });
 
  const performNavigation = (id: string) => {
    const parent = menuItems.find((p) =>
      p.children?.some((c) => c.id === id)
    );

    if (parent) {
      setActiveBorrowerId(id);
      setActiveLoanReferenceId(parent.id);
      return;
    }

    const item = menuItems.find((m) => m.id === id);

    if (item?.children?.length) {
      const firstChild = item.children[0];
      setActiveBorrowerId(firstChild.id);
      setActiveLoanReferenceId(id);
      return;
    }

    setActiveBorrowerId(undefined);
    setActiveLoanReferenceId(id);
  };

  const handleNavigationClick = (id: string) => {
    if (hasChanges) {
      setPendingNavigation(id);
      return;
    }
    performNavigation(id);
  };

  const handleDiscardChanges = () => {

    if (pendingNavigation) {
      performNavigation(pendingNavigation);
    }

    setPendingNavigation(null);
  };

  const handleContinueEditing = () => {
    setPendingNavigation(null);
  };


  if (loadingState === 'error') {
    return <ErrorDisplay error={"Failed to fetch borrower verifications"} onRetry={handleRetry} />;
  }

  if (loadingState === 'loading') {
    return (
      <InlineLoadingSpinner
        message="Loading borrower verifications..."
        subtitle={RETRIEVING_DATA_SUBTITLE}
        className="min-h-[400px]"
      />
    );
  }

  if (!loansAndBorrowers.length) {
    return (
      <div className="p-6 text-center border rounded-lg">
        <h3 className="font-semibold text-lg mb-2">No Loans Found</h3>
        <p className="text-sm">
          This case does not have any associated loans.
        </p>
      </div>
    );
  }


  return (
    <div >
      <div className="flex gap-[16px] overflow-hidden">
        {/* LEFT SIDE NAV */}
        <div className="w-[280px] min-w-[280px] bg-[#F1F5F9]">
          <AutoSelectVerticalNav
            title="Loan Accounts"
            items={menuItems}
            selectedId={activeBorrowerId ?? activeLoanReferenceId}
            onSelectionChange={handleNavigationClick}
          />
        </div>

        <div className="flex-1 min-w-0">
          <ProfileIdentityVerificationContainer
            case_id={case_id!}
            loanReferenceId={activeLoanReferenceId}
            borrowerId={activeBorrowerId}
            setHasChanges={setHasChanges}
          />
        </div>
      </div>

      <div className="h-px w-full bg-[#E5E5E5] my-4"></div>

      <AIRemarksContainer
        remarkKey={AnalysisRemarkKeys.ProfileIdentity}
        initialValue={initialValue}
        {...remarksProps}
      />

      {pendingNavigation && (
        <InterviewUnSavedChangesModal
          onAction={(mode) => {
            if (mode === "DISCARD") {
              handleDiscardChanges();
            } else {
              handleContinueEditing();
            }
          }}
        />
      )}
    </div>
  );
};

export default ProfileIdentityAccordionContent;
